<script setup lang="ts">
import {
  instanceOfBeamShapingGroupCircularDto,
  instanceOfBeamShapingGroupRectangularDto,
  ISystemSetupCombinationValue,
  NumericValueDto,
} from '@/api/generator';
import { hasTranslationKey } from '@/utils/helper';
import { I18nKey } from '@/utils/typings';
import SetupParameterDetails from '@/views/tasks/components/SetupParameterDetails.vue';
import { getSetupParameterName } from '@/views/tasks/utils';
import { useTranslation } from 'i18next-vue';
import { Dictionary, groupBy } from 'lodash';
import { computed } from 'vue';

const props = defineProps<{
  title?: string;
  parameters?: ISystemSetupCombinationValue[];
}>();

const { t } = useTranslation();

const headers = [
  {
    key: 'title',
    align: 'start',
    sortable: false,
  },
  {
    key: 'value',
    align: 'end',
    sortable: false,
  },
] as const;

function getGroupName(groupName?: string) {
  if (groupName !== 'undefined') {
    return hasTranslationKey(groupName) ? t(groupName as I18nKey) : groupName;
  }
}

const groups = computed<
  Dictionary<{
    groupName: string;
    items: {
      title: string;
      value: ISystemSetupCombinationValue | NumericValueDto | undefined;
    }[];
  }>
>(() =>
  Object.entries(groupBy(props.parameters, 'groupName')).reduce(
    (acc, [groupName, params]) => ({
      ...acc,
      [groupName]: {
        groupName: getGroupName(groupName),
        items: params.reduce<
          {
            title: string;
            value: ISystemSetupCombinationValue | NumericValueDto | undefined;
          }[]
        >((innerAcc, param) => {
          if (instanceOfBeamShapingGroupRectangularDto(param)) {
            // convert BeamShapingGroupRectangularDto to two seperate key value pairs
            innerAcc.push({
              title: getSetupParameterName(param.radiationFieldSizeX),
              value: param.radiationFieldSizeX,
            });
            innerAcc.push({
              title: getSetupParameterName(param.radiationFieldSizeY),
              value: param.radiationFieldSizeY,
            });
          } else if (instanceOfBeamShapingGroupCircularDto(param)) {
            // convert BeamShapingGroupCircularDto to a single key value pair
            innerAcc.push({
              title: getSetupParameterName(param.radiationFieldSize),
              value: param.radiationFieldSize,
            });
          } else {
            innerAcc.push({
              title: getSetupParameterName(param),
              value: param,
            });
          }
          return innerAcc;
        }, []),
      },
    }),
    {},
  ),
);
</script>

<template>
  <template v-if="typeof parameters?.length === 'number' && parameters?.length > 0">
    <h5 v-if="title" class="mb-0">{{ title }}</h5>

    <template v-for="group in groups" :key="group.groupName">
      <h6 v-if="group.groupName" class="mb-0 mt-3">{{ $t('Group_one') }}: {{ group.groupName }}</h6>

      <v-data-table :headers="headers" :items="group.items" :itemsPerPage="-1" hideDefaultFooter>
        <template #item.title="{ value }">
          <v-label class="text-justify">{{ value }}</v-label>
        </template>

        <template #item.value="{ value }">
          <SetupParameterDetails :item="value" />
        </template>
      </v-data-table>
    </template>
  </template>
</template>

<style scoped lang="scss">
:deep(.v-data-table__td) {
  vertical-align: top;
  padding-left: 0 !important;
  padding-top: 8px !important;
  padding-bottom: 8px !important;
  min-height: unset !important;
  height: unset !important;
}
</style>
